import { createInfiniteQuery, useQueryClient } from '@tanstack/svelte-query';

export interface InfiniteApiQueryOptions<T> {
	queryKey: unknown[];
	// Fetches one page of the list at the given offset — a website-api call
	// in practice, which is what distinguishes this from $global's
	// GraphQL-document-keyed `infiniteQuery`.
	queryFn: (offset: number) => Promise<T>;
	limit: number;
	maxItems?: number;
	initialOffset?: number;
}

// The REST counterpart of $global/utils/infinite-loading/infiniteQuery,
// with the same paging behaviour (offset pages of `limit`, stopping at
// `maxItems`) and the same eager first fetch — a server load that seeded the
// cache under the same key keeps it from refetching, exactly as before.
export const infiniteApiQuery = <T>({
	queryKey,
	queryFn,
	limit,
	maxItems = 0,
	initialOffset = 0,
}: InfiniteApiQueryOptions<T>) => {
	const queryClient = useQueryClient();

	queryClient.fetchInfiniteQuery({
		queryKey,
		queryFn: ({ pageParam }) => queryFn(pageParam as number),
		initialPageParam: initialOffset,
	});

	return createInfiniteQuery(() => ({
		queryKey,
		initialPageParam: initialOffset,
		queryFn: ({ pageParam }) => queryFn(pageParam as number),
		getNextPageParam: (lastPage, pages) => {
			let nextOffset: number | undefined = 0;

			if (lastPage && pages) {
				nextOffset = limit * pages.length + initialOffset;

				if (maxItems && nextOffset > maxItems) {
					nextOffset = undefined;
				}
			}

			return nextOffset;
		},
	}));
};
